import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, MessageCircle, User as UserIcon, LogIn, ChevronDown, Flower2, PawPrint, Sparkles, Search } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";
import AuthDialog from "@/components/AuthDialog";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import SearchSuggestionsList from "@/components/SearchSuggestionsList";
import { useTourSuggestions } from "@/hooks/useTourSuggestions";
import { useRecentSearches } from "@/hooks/useRecentSearches";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/tours", label: "Tours" },
  { to: "/destinations", label: "Destinations" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/testimonials", label: "Reviews" },
  { to: "/contact", label: "Contact" },
];

const THEMES = [
  { slug: "yoga", label: "Yoga & Wellness", desc: "Rishikesh, Kerala, Mysore", Icon: Flower2 },
  { slug: "wildlife", label: "Wildlife Safaris", desc: "Ranthambore, Bandhavgarh, Kaziranga", Icon: PawPrint },
  { slug: "festivals", label: "Festivals of India", desc: "Holi, Diwali, Pushkar Fair", Icon: Sparkles },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [mobileSearchQuery, setMobileSearchQuery] = useState("");
  const [showDesktopSuggestions, setShowDesktopSuggestions] = useState(false);
  const [showMobileSuggestions, setShowMobileSuggestions] = useState(false);
  const desktopSuggestions = useTourSuggestions(searchQuery);
  const mobileSuggestions = useTourSuggestions(mobileSearchQuery);
  const { recent: recentSearches, addSearch, removeSearch } = useRecentSearches();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => setSearchOpen(false), [location.pathname]);

  const runSearch = (query) => {
    const q = query.trim();
    if (!q) return;
    addSearch(q);
    navigate(`/tours?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setSearchQuery("");
    setMobileSearchQuery("");
  };

  const isHome = location.pathname === "/";
  const transparent = isHome && !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        transparent
          ? "bg-transparent"
          : "bg-white/85 backdrop-blur-md border-b border-cream-200 shadow-sm"
      }`}
    >
      <div className="container-tti flex items-center justify-between h-20 md:h-28 gap-3 md:gap-6">
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0 min-w-0" data-testid="navbar-logo">
          <img
            src={LOGO_URL}
            alt="Tour Trips India Holidays"
            className="h-12 sm:h-14 md:h-20 w-auto object-contain transition-transform group-hover:scale-105"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item, idx) => (
            <React.Fragment key={item.to}>
              <NavLink
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium tracking-wide transition-colors link-underline ${
                    transparent
                      ? "text-white hover:text-marigold"
                      : isActive
                      ? "text-saffron"
                      : "text-foreground hover:text-saffron"
                  }`
                }
              >
                {item.label}
              </NavLink>
              {/* Themes dropdown appears right after "Tours" */}
              {item.label === "Tours" && (
                <div className="relative group" data-testid="nav-themes-wrapper">
                  <button
                    data-testid="nav-themes-trigger"
                    className={`inline-flex items-center gap-1 text-sm font-medium tracking-wide transition-colors link-underline ${
                      transparent
                        ? "text-white hover:text-marigold"
                        : location.pathname.startsWith("/themes")
                        ? "text-saffron"
                        : "text-foreground hover:text-saffron"
                    }`}
                    aria-haspopup="true"
                  >
                    Themes
                    <ChevronDown className="w-3.5 h-3.5 opacity-70 transition-transform group-hover:rotate-180" />
                  </button>
                  <div
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[360px] opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50"
                    data-testid="nav-themes-panel"
                  >
                    <div className="bg-white border border-cream-200 rounded-sm shadow-xl overflow-hidden">
                      {THEMES.map(({ slug, label, desc, Icon }) => (
                        <NavLink
                          key={slug}
                          to={`/themes/${slug}`}
                          data-testid={`nav-theme-${slug}`}
                          className={({ isActive }) =>
                            `flex items-start gap-4 px-5 py-4 border-b border-cream-200 last:border-0 transition-colors ${
                              isActive ? "bg-cream" : "hover:bg-cream/60"
                            }`
                          }
                        >
                          <div className="mt-0.5 w-9 h-9 rounded-sm bg-saffron/10 text-saffron flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5" strokeWidth={1.75} />
                          </div>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-foreground">{label}</div>
                            <div className="text-[12px] text-foreground/60 mt-0.5 leading-snug">{desc}</div>
                          </div>
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          <div className="relative hidden lg:flex items-center" data-testid="navbar-search-wrapper">
            {searchOpen ? (
              <form
                onSubmit={(e) => { e.preventDefault(); runSearch(searchQuery); }}
                className="flex items-center bg-white rounded-sm border border-cream-200 shadow-lg overflow-hidden animate-fade-in"
              >
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowDesktopSuggestions(true)}
                  onBlur={() => setShowDesktopSuggestions(false)}
                  placeholder="Search tours, e.g. Kerala, Wildlife…"
                  data-testid="navbar-search-input"
                  className="w-56 h-10 px-3 text-sm text-foreground bg-transparent focus:outline-none"
                />
                <button type="submit" data-testid="navbar-search-submit" className="h-10 w-10 flex items-center justify-center text-saffron hover:bg-cream">
                  <Search className="w-4 h-4" />
                </button>
                <button type="button" onClick={() => setSearchOpen(false)} data-testid="navbar-search-close" className="h-10 w-10 flex items-center justify-center text-foreground/50 hover:text-deepred">
                  <X className="w-4 h-4" />
                </button>
              </form>
            ) : (
              <button
                onClick={() => setSearchOpen(true)}
                data-testid="navbar-search-toggle"
                aria-label="Search tours"
                className={`p-2 rounded-sm transition-colors ${transparent ? "text-white hover:text-marigold" : "text-foreground hover:text-saffron"}`}
              >
                <Search className="w-5 h-5" />
              </button>
            )}
            {searchOpen && showDesktopSuggestions && (
              <SearchSuggestionsList
                query={searchQuery}
                suggestions={desktopSuggestions}
                recent={recentSearches}
                testidPrefix="navbar-suggestion"
                onSelect={() => { setSearchOpen(false); setSearchQuery(""); }}
                onSelectRecent={(q) => runSearch(q)}
                onRemoveRecent={removeSearch}
              />
            )}
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <CurrencySwitcher transparent={transparent} />
            <LanguageSwitcher transparent={transparent} />
          </div>
          <a
            href="https://wa.me/917017798524"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-whatsapp"
            className={`hidden lg:inline-flex items-center gap-2 px-4 py-2 rounded-sm border text-sm font-medium transition-all ${
              transparent
                ? "border-white/40 text-white hover:bg-white hover:text-deepred"
                : "border-saffron text-saffron hover:bg-saffron hover:text-white"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            WhatsApp
          </a>
          {user && user.role !== "admin" ? (
            <Link
              to="/profile"
              data-testid="navbar-profile"
              className={`hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-sm transition-all ${
                transparent ? "bg-white text-deepred hover:bg-marigold" : "bg-deepred text-white hover:bg-deepred-700"
              }`}
            >
              <UserIcon className="w-4 h-4" /> {(user.full_name || user.name || "Profile").split(" ")[0]}
            </Link>
          ) : !user || user === false ? (
            <button
              onClick={() => { setAuthMode("signup"); setAuthOpen(true); }}
              data-testid="navbar-signin"
              className={`hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-sm font-medium text-sm uppercase tracking-wider transition-all whitespace-nowrap ${
                transparent ? "bg-white text-deepred hover:bg-marigold" : "bg-deepred text-white hover:bg-deepred-700"
              }`}
            >
              <LogIn className="w-4 h-4" /> Sign in
            </button>
          ) : null}
          <button
            onClick={() => setOpen(!open)}
            className={`lg:hidden p-2 flex-shrink-0 ${transparent ? "text-white" : "text-foreground"}`}
            data-testid="navbar-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-cream border-t border-cream-200 animate-fade-in" style={{ animationDuration: "220ms" }}>
          <div className="container-tti py-6 flex flex-col gap-4">
            <div className="relative" data-testid="mobile-search-wrapper">
              <form
                onSubmit={(e) => { e.preventDefault(); runSearch(mobileSearchQuery); }}
                className="flex items-center bg-white rounded-sm border border-cream-200 overflow-hidden"
                data-testid="mobile-search-form"
              >
                <input
                  type="text"
                  value={mobileSearchQuery}
                  onChange={(e) => setMobileSearchQuery(e.target.value)}
                  onFocus={() => setShowMobileSuggestions(true)}
                  onBlur={() => setShowMobileSuggestions(false)}
                  placeholder="Search tours, e.g. Kerala, Wildlife…"
                  data-testid="mobile-search-input"
                  className="flex-1 h-11 px-3 text-sm text-foreground bg-transparent focus:outline-none"
                />
                <button type="submit" data-testid="mobile-search-submit" className="h-11 w-11 flex items-center justify-center text-saffron">
                  <Search className="w-4 h-4" />
                </button>
              </form>
              {showMobileSuggestions && (
                <SearchSuggestionsList
                  query={mobileSearchQuery}
                  suggestions={mobileSuggestions}
                  recent={recentSearches}
                  testidPrefix="mobile-suggestion"
                  onSelect={() => { setOpen(false); setMobileSearchQuery(""); }}
                  onSelectRecent={(q) => runSearch(q)}
                  onRemoveRecent={removeSearch}
                />
              )}
            </div>
            <div className="flex items-center gap-3" data-testid="mobile-switchers">
              <CurrencySwitcher menuAlign="left" />
              <LanguageSwitcher menuAlign="left" />
            </div>
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-base font-medium py-2 border-b border-cream-200 ${
                    isActive ? "text-saffron" : "text-foreground"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            {/* Mobile Themes section */}
            <div className="pt-2" data-testid="mobile-nav-themes">
              <div className="text-[10px] uppercase tracking-[0.25em] text-foreground/50 mb-2">Themes</div>
              <div className="flex flex-col">
                {THEMES.map(({ slug, label, Icon }) => (
                  <NavLink
                    key={slug}
                    to={`/themes/${slug}`}
                    data-testid={`mobile-nav-theme-${slug}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 py-2.5 border-b border-cream-200 ${
                        isActive ? "text-saffron" : "text-foreground"
                      }`
                    }
                  >
                    <Icon className="w-4 h-4 text-saffron" strokeWidth={1.75} />
                    <span className="text-sm font-medium">{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
            {user && user.role !== "admin" ? (
              <Link to="/profile" className="btn-primary" data-testid="mobile-profile">
                <UserIcon className="w-4 h-4 mr-2" /> My account
              </Link>
            ) : !user || user === false ? (
              <button onClick={() => { setAuthMode("signup"); setAuthOpen(true); }} className="btn-primary" data-testid="mobile-signin">
                <LogIn className="w-4 h-4 mr-2" /> Sign in
              </button>
            ) : null}
            <div className="flex gap-3 pt-2">
              <a
                href="https://wa.me/917017798524"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 btn-outline"
                data-testid="mobile-whatsapp"
              >
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
              </a>
              <a href="tel:+917017798524" className="flex-1 btn-outline" data-testid="mobile-call">
                <Phone className="w-4 h-4 mr-2" /> Call
              </a>
            </div>
          </div>
        </div>
      )}
      <AuthDialog open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </header>
  );
}
